// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { TerceraChartAction, TerceraChartActionEnum } from '../TerceraChartAction';
import { TerceraChartActionState } from '../TerceraChartActionState';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Resources } from '@shared/localizations/Resources';
import { ChartZoomRendererButtons } from '../Renderers/TerceraChartZoomRenderer';
import { ITerceraChartShowScreenType } from '../ITerceraChartShowScreenType';
import { TerceraChartScalingByRectangleRenderer } from '../Renderers/TerceraChartScalingByRectangleRenderer';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class TerceraChartBaseActionProcessor {
    constructor(chart) {
        this.StateChanged = new CustomEvent();
        this.OnActionProcessor = new CustomEvent();
        this.actionsProcessor = {};
        this.statesProcessor = {};
        this.chart = chart;
        this.initializeActions();
        this.initializeStates();
    }
    initializeActions() {
        this.actionsProcessor[TerceraChartActionEnum.Settings] = this.processSettingsAction;
        this.actionsProcessor[TerceraChartActionEnum.Zoom] = this.processZoomAction;
        this.actionsProcessor[TerceraChartActionEnum.ZoomX] = this.processZoomXAction;
        this.actionsProcessor[TerceraChartActionEnum.ZoomY] = this.processZoomYAction;
        this.actionsProcessor[TerceraChartActionEnum.SnapToCandle] = this.processSnapToCandleAction;
        this.actionsProcessor[TerceraChartActionEnum.ScalingByRect] = this.processScalingByRectAction;
        this.actionsProcessor[TerceraChartActionEnum.Cancel] = this.processCancelAction;
        this.actionsProcessor[TerceraChartActionEnum.AutoScale] = this.processAutoScaleAction;
        this.actionsProcessor[TerceraChartActionEnum.ManualScale] = this.processManualScaleAction;
        this.actionsProcessor[TerceraChartActionEnum.ScaleSelector] = this.processScaleSelectorAction;
        this.actionsProcessor[TerceraChartActionEnum.Crosshair] = this.processCrosshairAction;
        this.actionsProcessor[TerceraChartActionEnum.CloseContextMenu] = this.processCloseContextMenuAction;
    }
    initializeStates() {
        this.statesProcessor[TerceraChartActionEnum.Settings] = this.processSettingsState;
        this.statesProcessor[TerceraChartActionEnum.Zoom] = this.processZoomState;
        this.statesProcessor[TerceraChartActionEnum.SnapToCandle] = this.processSnapToCandleState;
        this.statesProcessor[TerceraChartActionEnum.ScalingByRect] = this.processScalingByRectState;
        this.statesProcessor[TerceraChartActionEnum.Cancel] = this.processCancelState;
        this.statesProcessor[TerceraChartActionEnum.AutoScale] = this.processAutoScaleState;
        this.statesProcessor[TerceraChartActionEnum.ManualScale] = this.processManualScaleState;
    }
    // Action's которые отключаются по повторному клику
    IsAutoCheckItem(action) {
        if (action.Type === TerceraChartActionEnum.Eraser || action.Type === TerceraChartActionEnum.ToolSelector) {
            return true;
        }
        if (action.Type === TerceraChartActionEnum.Zoom &&
            action.Details === ChartZoomRendererButtons.ScalingByRectangle) {
            return true;
        }
        if (action.Type === TerceraChartActionEnum.CreationTool) {
            return true;
        }
        if (action.Type === TerceraChartActionEnum.ChartTrading) {
            return true;
        }
        return false;
    }
    // #region Action processing.
    ProcessTerceraChartAction(action, recursive = false) {
        let processed = false;
        try {
            if (isNullOrUndefined(action)) {
                return false;
            }
            const state = this.GetTerceraChartActionState(action);
            if (!state.Enabled)
                return false;
            let active = null;
            if (typeof action.Details === 'boolean') {
                active = action.Details;
            }
            // Auto check items.
            if (this.IsAutoCheckItem(action) && !recursive && this.GetTerceraChartActionState(action).Active) {
                const recAction = TerceraChartAction.Create(TerceraChartActionEnum.Cancel, true);
                this.ProcessTerceraChartAction(recAction, true);
                return true;
            }
            // Если включаем какой нибудь режим,
            // все остальные нужно выключить, используем TerceraChartActionEnum.Cancel
            if (this.shouldCancelAction(action.Type) && !recursive) {
                const newAction = TerceraChartAction.Create(TerceraChartActionEnum.Cancel, true);
                this.ProcessTerceraChartAction(newAction, true);
            }
            processed = this.actionsProcessor[action.Type].call(this, action, active);
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
        }
        finally {
            // call only one time
            if (!recursive)
                this.StateChanged.Raise();
        }
        return processed;
    }
    // Get action state: checked, enabled, current image, localization key etc.
    GetTerceraChartActionState(action) {
        if (isNullOrUndefined(action)) {
            return null;
        }
        const actionState = new TerceraChartActionState();
        const handler = this.statesProcessor[action.Type];
        if (!isNullOrUndefined(handler)) {
            handler.call(this, actionState, action);
        }
        if (Resources.isHidden(actionState.locKey)) {
            actionState.Visible = false;
        }
        return actionState;
    }
    CreateContextMenuItem(action) {
        if (isNullOrUndefined(action)) {
            return null;
        }
        const state = this.GetTerceraChartActionState(action);
        if (isNullOrUndefined(state)) {
            return null;
        }
        return {
            type: NewContextMenuItemType.Default,
            text: state.locKey,
            tag: action,
            svgImage: state.svgImage,
            canCheck: action.CanCheck,
            selected: action.CanCheck ? false : state.Active,
            checked: !action.CanCheck ? false : state.Active,
            enabled: state.Enabled,
            visible: state.Visible,
            closeOnClick: action.CloseOnClick,
            event: () => this.ProcessTerceraChartAction(action)
        };
    }
    // #region Processors
    processSettingsAction() {
        const processed = false;
        this.chart.terceraChartPanelContext.ShowScreen(ITerceraChartShowScreenType.ChartSettings, null);
        return processed;
    }
    processZoomAction(action) {
        let processed = false;
        switch (action.Details) {
            case ChartZoomRendererButtons.ZoomIn:
            case ChartZoomRendererButtons.StepZoomIn:
                this.chart.mainWindow.ZoomIn(action.Details === ChartZoomRendererButtons.StepZoomIn);
                this.chart.moveScreenByX(this.chart.rightBarIndexIncludingEmptyBars);
                processed = true;
                break;
            case ChartZoomRendererButtons.ZoomOut:
            case ChartZoomRendererButtons.StepZoomOut:
                this.chart.mainWindow.ZoomOut(action.Details === ChartZoomRendererButtons.StepZoomOut);
                this.chart.moveScreenByX(this.chart.rightBarIndexIncludingEmptyBars);
                this.chart.checkBarsCountAfterEvents();
                processed = true;
                break;
            case ChartZoomRendererButtons.ScalingByRectangle:
                if (this.chart.mainWindow.GetRenderer(TerceraChartScalingByRectangleRenderer).Active) {
                    processed = this.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.Cancel, true));
                }
                else {
                    processed = this.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.ScalingByRect));
                }
                break;
            default:
                processed = false;
                break;
        }
        return processed;
    }
    processZoomXAction(action) {
        return this.chart.mainWindow.ZoomX(action.Details);
    }
    processZoomYAction(action) {
        return this.chart.mainWindow.ZoomY(action.Details);
    }
    processSnapToCandleAction() {
        const processed = false;
        if (this.chart != null) {
            this.chart.setSnapToCandle(!this.chart.getSnapToCandle());
        }
        return processed;
    }
    processScalingByRectAction(action, active) {
        let processed = false;
        const renderer = this.chart.mainWindow.GetRenderer(TerceraChartScalingByRectangleRenderer);
        const newValue = active !== null ? active : !renderer.Active;
        processed = renderer.Active !== newValue;
        renderer.Active = newValue;
        return processed;
    }
    processCancelAction(action) {
        let processed = false;
        processed =
            this.ProcessTerceraChartAction(TerceraChartAction.Create(TerceraChartActionEnum.ScalingByRect, false), true) || processed;
        return processed;
    }
    processAutoScaleAction(action) {
        const processed = false;
        const window = action.Details;
        if (!isNullOrUndefined(window)) {
            window.AutoScale = true;
        }
        return processed;
    }
    processManualScaleAction(action) {
        const processed = false;
        const window = action.Details;
        if (!isNullOrUndefined(window)) {
            window.AutoScale = false;
        }
        return processed;
    }
    processScaleSelectorAction(action) {
        const processed = false;
        const details = action.Details;
        if (details.Point) {
            const point = details.Point;
            showContextMenu({
                x: point.X,
                y: point.Y,
                items: details.Items,
                functions: {
                    closeCallback: () => {
                        details.closeCallback();
                    }
                }
            });
        }
        else {
            this.OnActionProcessor.Raise(action);
        }
        return processed;
    }
    processCrosshairAction(action) {
        const processed = false;
        this.OnActionProcessor.Raise(action);
        return processed;
    }
    processCloseContextMenuAction() {
        hideContextMenu();
        return true;
    }
    // #endregion
    // #region States
    processSettingsState(outState) {
        outState.Enabled = true;
        outState.svgImage = 'menuSettings';
        outState.locKey = 'chart.layerManager';
    }
    processZoomState(outState, action) {
        const chart = this.chart;
        if (isNullOrUndefined(chart)) {
            return;
        }
        switch (action.Details) {
            case ChartZoomRendererButtons.StepZoomIn:
                outState.ToolTiplocKey = 'chart.chartScroll.zoomInButton';
                outState.Enabled = chart.mainWindow.CanZoomIn();
                break;
            case ChartZoomRendererButtons.StepZoomOut:
                outState.ToolTiplocKey = 'chart.chartScroll.zoomOutButton';
                outState.Enabled = chart.mainWindow.CanZoomOut();
                break;
            case ChartZoomRendererButtons.ScalingByRectangle:
                outState = chart.TerceraChartActionProcessor.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.ScalingByRect));
                break;
        }
    }
    processSnapToCandleState(outState) {
        outState.locKey = 'chart.tools.snapToCandle';
        const chart = this.chart;
        if (!isNullOrUndefined(chart)) {
            outState.Active = chart.getSnapToCandle();
        }
        else {
            outState.Active = false;
        }
    }
    processScalingByRectState(outState) {
        outState.ToolTiplocKey = 'chart.tools.ScalingByRect';
        const renderer = this.chart.mainWindow.GetRenderer(TerceraChartScalingByRectangleRenderer);
        outState.Active = renderer ? renderer.Active : false;
    }
    processCancelState(outState) {
        outState.locKey = 'chart.tools.default';
        outState.Active = !this.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.ScalingByRect)).Active;
    }
    processAutoScaleState(outState, action) {
        const window = action.Details;
        if (!isNullOrUndefined(window)) {
            outState.Active = window.AutoScale;
        }
        outState.locKey = 'chart.priceScaleContextMenu.AutoScale';
        outState.ToolTiplocKey = 'chart.chartWindowManager.SetManual';
    }
    processManualScaleState(outState, action) {
        const window = action.Details;
        if (!isNullOrUndefined(window)) {
            outState.Active = !window.AutoScale;
        }
        outState.locKey = 'chart.priceScaleContextMenu.ManualScale';
        outState.ToolTiplocKey = 'chart.chartWindowManager.SetAuto';
    }
    // #endregion
    shouldCancelAction(actionType) {
        return (actionType !== TerceraChartActionEnum.Cancel &&
            actionType !== TerceraChartActionEnum.Zoom &&
            actionType !== TerceraChartActionEnum.ZoomX &&
            actionType !== TerceraChartActionEnum.ZoomY &&
            actionType !== TerceraChartActionEnum.SnapToCandle &&
            !this.GetTerceraChartActionState(TerceraChartAction.Create(TerceraChartActionEnum.Cancel)).Active);
    }
}
